/**
 * Tab vocabulary for the CM/RM/RA Manager Pipeline Ledger.
 *
 * Mirrors the structure of partner-side `dealTabs.ts` but the tab semantics
 * are operationally-driven (what backlog needs adjudicating?), not lifecycle-
 * driven (what stage is my deal in?). Kept as a parallel file rather than
 * shoehorned into a shared module — the tab→status mapping is different
 * enough that one shared union would always be branching by mode.
 *
 * CHP-222 (RACCPE-20) collapsed the old four-segment vocabulary
 * (All / New Registrations / Extensions / Closed-Won Reviews) into three
 * filters — All deals | Active | To be reviewed. "Active" is the LIVE
 * (approved/in-flight) deals; "To be reviewed" unifies the three review
 * backlogs (registration / extension / closed-win). The tabs are a
 * client-side filter over the single `manager-list?segment=all` fetch, so
 * the union is local to the frontend and no longer the API `ManagerDealSegment`.
 */

import type { DealDashboardMetrics, ManagerDealListItem } from "@/api/client";

// CHP-364: the tab IDs stay stable (active/to_be_reviewed) to avoid route +
// test churn; the user-facing labels are Live / Pending Review, plus dedicated
// Closed-won + Archive tabs.
export type ManagerDealTab = "all" | "active" | "to_be_reviewed" | "closed_won" | "archive";

export const MANAGER_DEAL_TAB_ORDER: readonly ManagerDealTab[] = [
  "all",
  "active",
  "to_be_reviewed",
  "closed_won",
  "archive",
];

export const MANAGER_DEAL_TAB_LABEL: Record<ManagerDealTab, string> = {
  all: "All Deals",
  active: "Live",
  to_be_reviewed: "In review",
  closed_won: "Closed-won",
  archive: "Archive",
};

/** Statuses each tab keeps when filtering client-side. The `all` tab keeps
 * everything the backend returns — the active/review statuses PLUS the terminal
 * statuses (CLOSED_WON / CLOSED_LOST / DENIED), so their detail views are
 * reachable (CHP-269 pt2; makes the CHP-241/246 views reachable). `active` is
 * the LIVE deals; `to_be_reviewed` unifies the three review backlogs. */
export const MANAGER_DEAL_TAB_STATUSES: Record<
  ManagerDealTab,
  readonly ManagerDealListItem["deal_status"][]
> = {
  all: [
    "pending_review",
    "live",
    "extension_pending",
    "win_pending_review",
    "closed_won",
    "closed_lost",
    "denied",
  ],
  active: ["live"],
  to_be_reviewed: ["pending_review", "extension_pending", "win_pending_review"],
  closed_won: ["closed_won"],
  archive: ["closed_lost", "denied"],
};

export function managerDealMatchesTab(
  item: Pick<ManagerDealListItem, "deal_status">,
  tab: ManagerDealTab,
): boolean {
  return MANAGER_DEAL_TAB_STATUSES[tab].includes(item.deal_status);
}

/** Tab bracket counts sourced from the shared dashboard metrics — same
 * cache entry that feeds the scorecards. The `all` tab sums the LIVE deals,
 * the three review backlogs, and the terminal statuses so the bracket equals
 * the rendered row count; `active` is the LIVE count; `to_be_reviewed` sums
 * the backlogs. */
export function managerDealTabCount(metrics: DealDashboardMetrics, tab: ManagerDealTab): number {
  const toBeReviewed =
    metrics.pending_review + metrics.extension_pending + metrics.win_pending_review;
  const terminal = metrics.won_deals + metrics.lost_deals + metrics.denied_deals;
  switch (tab) {
    case "all":
      return metrics.active_deals + toBeReviewed + terminal;
    case "active":
      return metrics.active_deals;
    case "to_be_reviewed":
      return toBeReviewed;
    case "closed_won":
      return metrics.won_deals;
    case "archive":
      return metrics.lost_deals + metrics.denied_deals;
  }
}
